<template>
  <div class="quick-page">
    <div
      class="modal quick-page__modal items-start"
      :style="modalStyle"
      @mousedown="closeQuickPage"
    >
      <div
        class="quick-page-popup quick-page__popup box-border"
        @mousedown.stop
      >
        <div class="quick-page__header">
          <div class="quick-page__header-option grow">
            <div class="quick-page__header-option-label text-gray-400">
              {{ translate('global.label.createIn') }}
            </div>
            <div class="quick-page__header-option-control flex items-center">
              <EmojiIcon
                v-if="icon.type === emojiEnum"
                :emoji="icon.src"
                :classes="'flex justify-center items-center'"
              />
              <em
                v-else
                class="quick-page__header-option-control-icon flex items-center"
                :style="iconStyle"
              />
              <div class="quick-page__header-option-control-field">
                <ListDropdownWrap
                  ref="projectDropdownQP"
                  :dropdown-width="dropdownWidth"
                  :height="dropdownInitialHeight"
                  :items="dropdownItems"
                  :selected-item="selectedProjectIndex"
                  :empty-list-text="translate('noBoardsInSpace')"
                  :dropdown-style="{
                    maxHeight: `${dropdownHeight}px`,
                  }"
                  @item-click="handleAccountClick"
                />
              </div>
            </div>
            <ApplyTemplate
              class="quick-page__header-option-control"
              :resource-type="resourceType"
              @selectTemplate="handleTemplateSelect"
            />
          </div>
          <div class="quick-page__header-toggle">
            <div class="quick-page__header-toggle-label text-gray-400">
              {{ translate('quickPage.label.setAsDefaultSpace') }}
            </div>
            <div class="quick-page__header-toggle-control flex items-center">
              <ToggleButton
                :default-value="toggleValue"
                @click="toggleDefault"
              />
            </div>
          </div>
          <div class="quick-page__header-close">
            <EscTooltip
              :label="'global.text.discardDraft'"
              :tooltip-meta="{}"
              @closePopup="closeQuickPageAndClearTempData"
            />
          </div>
        </div>
        <div class="quick-page__title-input">
          <AutoExpandTextarea
            ref="quickPageTitleInput"
            :value="tempTitle"
            class="quick-page__title-input-control text-gray-900"
            :text-value="tempTitle"
            :textarea-class="'quick-page__title-input-control'"
            :initial-height="titleInputInitialHeight"
            :placeholder="translate('pageTitle')"
            @input="storeTempValue"
            @enter="focusDescription"
            @esc="closeQuickPage"
            @arrowDownRightEnd="focusDescription"
            @tabpress="focusDescription"
          />
        </div>
        <vue-scroll
          :ops="{
            scrollPanel: {
              scrollingX: false,
            },
            bar: {
              background: spfColor.gray200,
              keepShow: true,
            },
          }"
          class="quick-page__content-input flex overflow-hidden flex-col"
        >
          <Editor
            v-if="editorReady"
            ref="quickPageDesc"
            :resource-type="resourceType"
            :resource-id="'quick'"
            :content="pageContent"
            :watch-content="false"
            :input-place-holder="translate('global.placeholder.pagePlaceholder')"
            :bottom-gutter="true"
            :draggable="false"
            :handle-arrow-left="true"
            @input="textChange"
            @arrow-left-at-doc-start="focusTitle"
            @esc.stop="closeQuickPage"
          />
        </vue-scroll>
        <div class="quick-page__actions flex justify-end">
          <v-popover>
            <ButtonCustom
              :text="translate('global.text.create')"
              :type="'important'"
              class="quick-page__actions-item"
              @click="createPage"
            />
            <template #content>
              <TooltipTemplate
                :tooltip-text="translate('global.text.create')"
                :tooltip-meta="{ icon: { ctrl: true, key: 'Enter' } }"
              />
            </template>
          </v-popover>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import AutoExpandTextarea from '@/components/elements/AutoExpandTextarea.vue';
import ListDropdownWrap from '@/components/widgets/ListDropdownWrap.vue';
import TooltipTemplate from '@/components/elements/TooltipTemplate.vue';
import ButtonCustom from '@/components/elements/ButtonCustom.vue';
import getDashedTitle from '@/utilities/getDashedTitle';
import ToggleButton from '@/components/elements/ToggleButton.vue';
import EscTooltip from '@/components/widgets/EscTooltip.vue';
import EmojiIcon from '@/components/functional/EmojiIcon.vue';
import customIconMixin from '@/mixins/customIconsMixin';
import { defineComponent, defineAsyncComponent } from 'vue';
import { spfColor } from '@/assets/scss/variables';
import {
  browserStorageGetItem,
  browserStorageSetItem,
  browserStorageRemoveItem,
} from '@/utilities/browser-storage.util';
import EventBus from '@/utilities/eventBus';
import electronHelpersMixin from '@/mixins/electronHelpersMixin';
import { ResourceType } from '@/types/resources';
import ApplyTemplate from '@/components/widgets/ApplyTemplate.vue';
import appendToEditor from '@/utilities/appendToEditor';
import checkPlatform from '@/mixins/checkPlatform';
import { nanoid } from 'nanoid';
import { quickPageNoteKey } from '@/types/notes';

const quickPageBrowserStorageKey = 'quick-page-temp-value';
interface IQuickPageTempStoredValue {
  title: string;
  content: string;
  project_id: string;
  getTeamID: string;
}
export default defineComponent({
  name: 'QuickPage',
  components: {
    AutoExpandTextarea,
    ListDropdownWrap,
    TooltipTemplate,
    ButtonCustom,
    EscTooltip,
    ToggleButton,
    Editor: defineAsyncComponent(
      () => import(/* webpackChunkName: 'Editor' */ '@/components/editor/Editor.vue')
    ),
    EmojiIcon,
    ApplyTemplate,
  },
  mixins: [customIconMixin, electronHelpersMixin, checkPlatform],
  data(): any {
    return {
      titleInputInitialHeight: 34,
      inputValue: '',
      pageContent: '',
      tempTitle: '',
      dropdownItems: [],
      selectedProjectIndex: 0,
      toggleValue: false,
      selectedProjectId: '',
      defaultPage: null,
      updatedLocalStorageProjectId: null,
      projectIds: [],
      dropdownHeight: 0,
      dropdownWidth: 250,
      dropdownInitialHeight: 18,
      spfColor: spfColor,
      resourceType: ResourceType.Page,
      editorReady: false,
      isShiftKey: false,
    };
  },
  computed: {
    currentProjectId(): string {
      return this.$store.getters.getCurrentProjectId;
    },
    modalStyle(): {} {
      if (this.$store.getters.getPreviewAttachmentIndex !== -1) {
        return {
          'z-index': 5,
        };
      }
      return {};
    },
    teamSlug(): string {
      return this.$store.getters.getTeamSlug;
    },
    localStorageProjectId(): any {
      return localStorage.getItem(`${this.getTeamId}-defaultPageId`);
    },
    getTeamId(): string {
      return this.$store.getters.getTeam.id;
    },
    icon() {
      return this.dropdownItems[this.selectedProjectIndex]?.parentIcon || {};
    },
    iconStyle(): any {
      const icon = this.icon;
      return {
        ...this.getCustomIconStyle(this.iconPath(icon.src || '')),
        background: icon.color,
      };
    },

    localStorageKey() {
      return this.$store.getters.getNoteInQuickPage ? quickPageNoteKey : quickPageBrowserStorageKey;
    },
  },

  watch: {
    '$route.path': {
      handler() {
        if (this.$store.getters.getQuickPage) {
          this.$store.dispatch('setQuickPage', false);
        }
      },
    },
  },

  created(): void {
    this.toggleValue = !!this.localStorageProjectId;
    window.addEventListener('resize', this.setDropdownHeight);
  },
  beforeMount(): void {
    this.toggleValue = !!this.localStorageProjectId;
  },
  mounted(): void {
    this.setDropdownHeight();
    this.$store.dispatch('setShowDuplicateCardPopup', '');
    this.updatedLocalStorageProjectId = this.localStorageProjectId;
    window.addEventListener('keydown', this.keyHandler);
    const projects = this.$store.getters.getMySortedProjects;
    projects.forEach((project: any) => {
      this.dropdownItems.push({
        id: project.id,
        label: project.title,
        parentIcon: project.icon,
        iconType: project.icon?.type,
        emojiIcon: project.icon?.src,
        ...(project.icon?.src && {
          customIconName: this.iconPath(project.icon.src),
        }),
        ...(project.icon?.color && { customIconColor: project.icon.color }),
        privateItem: project?.is_private,
      });
    });
    this.dropdownItems.forEach((element: any) => {
      this.projectIds.push(element.id);
      if (this.localStorageProjectId === element.id) {
        this.defaultPage = element;
        this.selectedProjectId = this.localStorageProjectId;
        this.selectedProjectIndex = this.dropdownItems.indexOf(element);
        this.toggleValue = true;
      }
      if (!this.defaultPage && element.id === this.currentProjectId) {
        this.selectedProjectIndex = this.dropdownItems.indexOf(element);
        this.selectedProjectId = this.currentProjectId;
      }
    });

    if (!this.selectedProjectId && this.dropdownItems.length > 0) {
      this.selectedProjectId = this.dropdownItems[0].id;
      this.selectedProjectIndex = 0;
    }

    if (!this.projectIds.includes(this.localStorageProjectId) && this.localStorageProjectId) {
      this.defaultPage = {};
      this.toggleValue = false;
      localStorage.removeItem(`${this.getTeamId}-defaultPageId`);
    }
    this.checkBrowserStorageAndUpdateValues();
    this.focusTitle();
    this.editorReady = true;
  },
  beforeUnmount(): void {
    window.removeEventListener('keydown', this.keyHandler);
    window.removeEventListener('resize', this.setDropdownHeight);
    this.$store.dispatch('setNoteInQuickPage', '');
  },
  methods: {
    checkBrowserStorageAndUpdateValues(): void {
      const storedValue: IQuickPageTempStoredValue | null =
        browserStorageGetItem<IQuickPageTempStoredValue | null>(this.localStorageKey);
      const isSameTeamSubdomain = storedValue?.getTeamID === this.$store.getters.getTeamID;
      if (!!storedValue && isSameTeamSubdomain) {
        const { title, content, project_id } = storedValue;
        this.tempTitle = title;
        this.pageContent = content;
        this.inputValue = content;
        this.selectedProjectId = project_id;
        this.selectedProjectIndex = this.dropdownItems.indexOf(
          this.dropdownItems.filter((project: any) => project.id === project_id)[0]
        );
        this.toggleValue = this.localStorageProjectId === project_id;
      }
      if (!isSameTeamSubdomain) {
        this.clearStoredTempValue();
      }
    },
    clearStoredTempValue(): void {
      browserStorageRemoveItem(this.localStorageKey);
    },
    storeTempValue(event: any): void {
      this.tempTitle = event?.target?.value ?? this.tempTitle;
      if (this.tempTitle || this.inputValue) {
        // only if user enters a title or some content we store the value into browser storage
        const tempPageValue: IQuickPageTempStoredValue = {
          title: this.tempTitle,
          content: this.inputValue,
          project_id: this.selectedProjectId,
          getTeamID: this.$store.getters.getTeamID,
        };
        browserStorageSetItem(this.localStorageKey, tempPageValue);
      }
    },
    setDropdownHeight(): void {
      // 50px as a bottom padding
      this.dropdownHeight = window.innerHeight - (this.$refs.projectDropdownQP.$el.offsetTop + 50);
    },
    focusTitle() {
      requestAnimationFrame(() => {
        this.$refs.quickPageTitleInput?.focus();
      });
    },
    toggleDefault(): void {
      if (this.toggleValue) {
        this.defaultPage = null;
        this.toggleValue = false;
        localStorage.removeItem(`${this.getTeamId}-defaultPageId`);
        return;
      }
      this.dropdownItems.forEach((element: any) => {
        if (element.id === this.selectedProjectId) {
          this.defaultPage = element;
          localStorage.setItem(`${this.getTeamId}-defaultPageId`, this.selectedProjectId);
          this.updatedLocalStorageProjectId = this.selectedProjectId;
        }
      });
      this.toggleValue = !this.toggleValue;
    },
    shortcutEnter(eventKey: string): boolean {
      return ['Enter'].includes(eventKey);
    },
    keyHandler(event: KeyboardEvent): void {
      if (this.isApple) {
        if (event.metaKey && this.shortcutEnter(event.key)) {
          this.createPage();
          return;
        }
      }
      if (event.ctrlKey && this.shortcutEnter(event.key)) {
        this.createPage();
      }
      if (event.shiftKey && this.shortcutEnter(event.key)) {
        this.isShiftKey = true;
        this.createPage();
      }
    },
    focusDescription($event: any): void {
      $event.target.blur();
      this.$refs.quickPageDesc.focusEditor('start');
    },
    openPage(page: any): void {
      if (page && Object.keys(page).length) {
        if (['page', 'newpage'].includes(this.$route.name)) {
          if (this.$route.name === 'newpage') {
            this.$store.dispatch('setNewPageCreating', false);
          }
          this.$store.dispatch('setPage', page);
          this.$store.dispatch('setCurrentPageID', page.id);
        }
        const dashedPageTitle = getDashedTitle(page.title);
        if (this.$route.params.pageId !== page.id) {
          this.$router.push(`/${this.teamSlug}/page-${page.id}-${dashedPageTitle}`);
        }
      }
    },
    createPage(): void {
      if (!this.selectedProjectId) {
        // TODO: add notification for this
        // TODO: handle when there are no projects
        return;
      }
      this.$store.dispatch('pageCreating', true);
      const localPageId = nanoid(6);
      const newPage = {
        title: this.tempTitle,
        content: this.inputValue,
        project_id: this.selectedProjectId,
        id: localPageId,
        quickPage: true,
        schema: 0,
      };
      this.$store.dispatch('addPageToFlatPages', newPage);
      this.$store.dispatch('localAddPageInPageTree', {
        projectId: this.selectedProjectId,
        pageId: localPageId,
      });
      this.$store
        .dispatch('createNewPage', { page: newPage })
        .then((response: any) => {
          const { page } = response;
          if (this.isShiftKey) {
            this.openPage(page);
          }
          this.$store.dispatch('addPageToFlatPages', page);
          this.$store.dispatch('addPageToProjectPages', {
            pageId: page.id,
            projectId: this.selectedProjectId,
          });
          this.$store
            .dispatch('localRemovePageInTree', {
              projectId: this.selectedProjectId,
              pageId: localPageId,
            })
            .then(() => {
              this.$store.dispatch('localAddPageInPageTree', {
                projectId: this.selectedProjectId,
                pageId: page.id,
              });
              this.$store.dispatch('localAddPageInProject', {
                projectId: page.project_id,
                page,
              });
            });
          this.$store.dispatch('deleteFromPages', {
            localPageId,
            newPage,
          });
          this.$store.dispatch('pageCreating', false);
          this.$store.dispatch('addUiNotification', {
            message: this.translate('pageCreated'),
            labels: [
              {
                text: this.translate('open'),
                type: this.$constants.uiNotificationElements.button,
                callback: this.openPage,
                params: page,
                buttonStyle: 'important',
              },
            ],
          });

          const filesToUpload = this.$store.getters.getQuickPageFilesToUpdate;

          if (filesToUpload.length > 0) {
            filesToUpload.forEach((fileId: string) => {
              this.$store.dispatch('updateFile', {
                fileId,
                payload: {
                  page_id: page.id,
                },
              });
            });
            this.$store.dispatch('removeQuickPageFilesToUpdate');
          }

          EventBus.$emit('addQuickPageToRecentlyViewed', page);
        })
        .catch(() => {
          this.$store.dispatch('deleteFromPages', {
            localPageId,
            newPage,
          });
          this.$store.dispatch('localRemovePageInTree', {
            projectId: this.selectedProjectId,
            pageId: localPageId,
          });
          this.$store.dispatch('addUiNotification', {
            message: this.translate('actionFailed'),
            status: this.$constants.uiNotificationStatuses.error,
            duration: 4000,
          });
        });
      this.closeQuickPage();
      this.clearStoredTempValue();
    },
    handleAccountClick(item: any, index: number): void {
      if (index !== -1 && index !== this.selectedProjectIndex) {
        this.selectedProjectIndex = index;
      }
      this.selectedProjectId = item.id;
      if (this.updatedLocalStorageProjectId === item.id) {
        this.toggleValue = true;
      } else {
        this.toggleValue = false;
      }
      this.storeTempValue();
    },
    closeQuickPage() {
      this.$store.dispatch('setQuickPage', false);
    },
    closeQuickPageAndClearTempData(): void {
      this.closeQuickPage();
      this.clearStoredTempValue();
    },
    textChange(value: string) {
      this.inputValue = value;
      this.storeTempValue();
    },
    handleTemplateSelect(template: any) {
      if (!template) return;
      const { page_title: pageTitle, page_content: pageContent } = template;
      if (pageTitle) {
        this.tempTitle = pageTitle;
      }
      appendToEditor(pageContent, this.$refs.quickPageDesc);
      this.storeTempValue();
    },
  },
});
</script>

<style lang="scss">
@import '../../assets/scss/typography';
@import '../../assets/scss/mixins/index';
@import '../../assets/scss/breakpoints';
.quick-page {
  &__modal {
    padding-top: 13vh;
  }
  // overrides styles for <MarkdownInput /> component
  // start
  .st-editor .st-editor-content {
    border: none;
    padding: 8px 16px;
    min-height: 102px;
    @apply text-text-default;
  }
  // end
  .hide-popover {
    display: none;
  }
  &__popup {
    width: $wPopupPrimary;
    max-width: $mwPopupPrimary;
    padding: {
      top: $spaceTopSm;
      right: $spaceRightSm;
      bottom: $spaceBottomSm;
      left: $spaceLeftSm;
    }
    @include tablet {
      padding: {
        top: $spaceTopLg;
        right: $spaceRightLg;
        bottom: $spaceBottomLg;
        left: $spaceLeftLg;
      }
    }
    @apply bg-background-strongest border border-solid border-important-border rounded-lg;
  }
  &__title-input {
    padding: {
      top: $spaceTopLg;
      bottom: $spaceBottomLg;
    }
    @apply bg-background-strongest;
  }
  &__title-input-control {
    @include quickCardTitle();
    @apply bg-background-strongest text-text-default;
  }
  &__content-input {
    max-height: 40vh;
    min-height: 304px;
    width: calc(100% + #{$spaceRightPrimary} + #{$spaceLeftPrimary}) !important;
    // this code is to override editors paddings
    // start
    margin-right: -$spaceRightPrimary;
    margin-left: -$spaceLeftPrimary;
    // end
  }
  &__actions {
    padding-top: $spaceTopLg;
  }
  // component <ButtonCustom /> override
  // start
  &__actions-item {
    padding: 0;
    padding: {
      top: 0;
      right: $spaceRightMd;
      bottom: 0;
      left: $spaceLeftMd;
    }
    width: 75px;
    height: 32px;
  }
  // end
  &__header {
    @apply flex items-start;
    @include tablet {
      @apply items-center;
    }
  }
  &__header-toggle {
    @include tablet {
      @apply flex items-center;
    }
  }
  &__header-toggle-control {
    height: 28px; // nice vertical alignment in mobile view with &__header-option-control
    @include tablet {
      height: auto;
      padding-left: $spaceLeftSm;
    }
    // overrides styles for <MarkdownInput /> component
    // start
    .toggle-button {
      padding-left: 0;
    }
    // end
  }
  &__header-toggle-label {
    @apply text-text-default;
    @include textSmNormal;
  }
  &__header-option {
    @include tablet {
      @apply flex items-center;
    }
  }
  &__header-option-label {
    @include textSmMedium();
    @include tablet {
      @apply flex items-center;
    }
  }
  &__header-option-control {
    @include tablet {
      padding-left: $spaceLeftSm;
    }
  }
  &__header-option-control-icon {
    height: $szIconPrimary;
    width: $szIconPrimary;
  }
  &__header-option-control-field {
    max-width: 130px;
    padding-left: $spaceLeftSm;
    @include tablet {
      max-width: 150px;
    }
    // overrides for <ListDropdownWrap />
    // start
    .dropdown-wrap {
      .dropdown-button {
        background: transparent;
        padding: 0;
        border: none;
        box-shadow: none;
        .dropdown-wrap-arrow-down {
          margin-left: $spaceLeftXs;
        }
      }
    }
    .dropdown-list {
      max-height: calc(50vh + 100px);
      &:first-child {
        border: none;
      }
    }
    // end
  }
  &__header-close {
    padding-left: $spaceLeftLg;
    // overrides styles for <EscTooltip /> component
    // start
    .square-icon-wrap {
      margin-left: 0;
      margin-right: 0;
    }
    // end
  }
}
</style>
